import { useEffect, useState } from 'react';
import { Clock, Plus, X, Edit2, Trash2, Loader2, RotateCcw, Calendar, Users, Package, Eraser, ShoppingCart, ChevronDown, Check, MessageCircle, Printer } from 'lucide-react';
import { useStoreTimeSlots, useStoreOrders, updateOrderStatus } from '../hooks/useStoreApi';
import { useIsAppointments } from '../hooks/useVocab';

const DAYS_OF_WEEK = [
    { value: 0, label: 'ראשון', shortLabel: 'א\'' },
    { value: 1, label: 'שני', shortLabel: 'ב\'' },
    { value: 2, label: 'שלישי', shortLabel: 'ג\'' },
    { value: 3, label: 'רביעי', shortLabel: 'ד\'' },
    { value: 4, label: 'חמישי', shortLabel: 'ה\'' },
    { value: 5, label: 'שישי', shortLabel: 'ו\'' },
    { value: 6, label: 'שבת', shortLabel: 'ש\'' }
];

// Time Slots Tab Component
export const TimeSlotsTab = ({ storeId, onRegisterRefresh, chatId }) => {
    const isAppt = useIsAppointments();
    const { timeSlots, timeSlotsEnabled, loading, error, refetch, updateConfig, addSlot, updateSlot, deleteSlot, clearAllUsage } = useStoreTimeSlots(storeId, chatId);
    const { data: ordersData, refetch: refetchOrders } = useStoreOrders(storeId, null, chatId);
    // Only show active (not archived) orders
    const orders = (ordersData?.orders || []).filter(order => order.status !== 'archived');

    const [showModal, setShowModal] = useState(false);
    const [editingSlot, setEditingSlot] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [showOrdersModal, setShowOrdersModal] = useState(false);
    const [selectedSlotForOrders, setSelectedSlotForOrders] = useState(null);
    const [showSlotDetailsModal, setShowSlotDetailsModal] = useState(false);
    const [selectedSlotForDetails, setSelectedSlotForDetails] = useState(null);
    const [showAllTimeSlotsOrdersModal, setShowAllTimeSlotsOrdersModal] = useState(false);

    // Calculate order counts per time slot and group orders by slot
    const orderCountsBySlot = {};
    const ordersBySlot = {};
    orders.forEach(order => {
        // Orders may contain time slot info in various fields
        // Check if order has time slot data (time_slot_date and delivery_time indicate a slot was used)
        if (order.time_slot_id) {
            orderCountsBySlot[order.time_slot_id] = (orderCountsBySlot[order.time_slot_id] || 0) + 1;
            if (!ordersBySlot[order.time_slot_id]) {
                ordersBySlot[order.time_slot_id] = [];
            }
            ordersBySlot[order.time_slot_id].push(order);
        }
    });

    const handleViewSlotOrders = (slot) => {
        setSelectedSlotForOrders(slot);
        setShowOrdersModal(true);
    };

    const handleSlotClick = (slot) => {
        setSelectedSlotForDetails(slot);
        setShowSlotDetailsModal(true);
    };

    // Register refresh handler for Alt+R
    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('timeslots', async () => {
                await refetch();
                await refetchOrders();
            });
        }
    }, [onRegisterRefresh, refetch, refetchOrders]);

    const handleToggleEnabled = async () => {
        try {
            setSaving(true);
            setActionError(null);
            // Normalize time slots to ensure all numeric fields are integers
            const normalizedTimeSlots = timeSlots.map(slot => ({
                ...slot,
                day_of_week: parseInt(slot.day_of_week),
                max_orders: parseInt(slot.max_orders),
                max_items: parseInt(slot.max_items)
            }));
            await updateConfig({
                time_slots_enabled: !timeSlotsEnabled,
                time_slots: normalizedTimeSlots
            });
            await refetch();
        } catch (err) {
            setActionError(err.message || 'שגיאה בעדכון הגדרות');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (slot) => {
        if (!window.confirm('למחוק את משבצת הזמן הזו?')) return;
        try {
            setDeletingId(slot.slot_id);
            setActionError(null);
            await deleteSlot(slot.slot_id);
            await refetch();
        } catch (err) {
            setActionError(err.message || 'שגיאה במחיקה');
        } finally {
            setDeletingId(null);
        }
    };

    const openNewModal = () => {
        setEditingSlot(null);
        setShowModal(true);
    };

    const openEditModal = (slot) => {
        setEditingSlot(slot);
        setShowModal(true);
    };

    const handleClearAllUsage = async () => {
        if (!window.confirm('האם אתה בטוח שברצונך לאפס את כל השימוש במשבצות הזמן? פעולה זו תמחק את כל ההזמנות והפריטים שנרשמו בכל המשבצות.')) {
            return;
        }
        try {
            setClearing(true);
            setActionError(null);
            const result = await clearAllUsage();
            await refetch();
            alert(`אופס בהצלחה! `);
        } catch (err) {
            setActionError(err.message || 'שגיאה באיפוס משבצות הזמן');
        } finally {
            setClearing(false);
        }
    };

    // Group slots by day of week
    const slotsByDay = timeSlots.reduce((acc, slot) => {
        if (!acc[slot.day_of_week]) {
            acc[slot.day_of_week] = [];
        }
        acc[slot.day_of_week].push(slot);
        return acc;
    }, {});

    // Sort slots within each day by start_time
    Object.keys(slotsByDay).forEach(day => {
        slotsByDay[day].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });

    return (
        <div className="space-y-6 mt-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl font-bold text-mekomit-900">{isAppt ? 'זמינות לתורים' : 'משבצות זמן'}</h2>
                    <p className="text-sm text-mekomit-600">
                        {isAppt ? 'הגדר את שעות הקבלה שבהן מטופלים יכולים לקבוע תור' : 'הגדר שעות זמינות לאיסוף/משלוח עם הגבלת כמות הזמנות ופריטים'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleToggleEnabled}
                        disabled={saving}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-md border transition-all ${timeSlotsEnabled
                            ? 'bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white border-mekomit-300 hover:from-mekomit-600 hover:to-mekomit-500'
                            : 'bg-white text-mekomit-700 border-mekomit-300 hover:bg-mekomit-50'
                            }`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                        <span>{timeSlotsEnabled ? 'מופעל' : 'כבוי'}</span>
                    </button>
                    <button
                        onClick={openNewModal}
                        disabled={!timeSlotsEnabled}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white font-semibold shadow-md border border-mekomit-300 hover:from-mekomit-600 hover:to-mekomit-500 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4 text-white" />
                        <span>הוסף משבצת</span>
                    </button>
                    {/* <button
                        onClick={handleClearAllUsage}
                        disabled={clearing || !timeSlotsEnabled}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-danger-600 font-semibold border border-danger-300 hover:bg-danger-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="אפס שימוש"
                    >
                        {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eraser className="w-4 h-4" />}
                        <span>אפס שימוש</span>
                    </button> */}
                    <button
                        onClick={async () => {
                            await refetch();
                            await refetchOrders();
                        }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-mekomit-700 font-medium border border-mekomit-300 hover:bg-mekomit-50 transition-all"
                        title="רענן"
                    >
                        <RotateCcw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {actionError && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                    {actionError}
                </div>
            )}

            {!timeSlotsEnabled && (
                <div className="p-4 bg-mekomit-50 border border-mekomit-200 rounded-lg text-mekomit-700 text-sm">
                    משבצות הזמן כבויות. הפעל אותן כדי להתחיל להגדיר שעות זמינות.
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-card p-6 border border-mekomit-100">
                {loading ? (
                    <div className="flex items-center justify-center py-12 text-mekomit-600">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        טוען משבצות זמן...
                    </div>
                ) : error ? (
                    <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-800 text-sm">
                        שגיאה בטעינה: {error.message || error.toString()}
                    </div>
                ) : !timeSlots || timeSlots.length === 0 ? (
                    <div className="text-center py-12 text-mekomit-600">
                        <Clock className="w-10 h-10 mx-auto mb-4 text-mekomit-400" />
                        <p className="text-lg font-medium">אין עדיין משבצות זמן.</p>
                        <p className="text-sm mt-1">לחץ על "הוסף משבצת" כדי ליצור אחת.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {DAYS_OF_WEEK.map(day => {
                            const daySlots = slotsByDay[day.value] || [];
                            if (daySlots.length === 0) return null;

                            return (
                                <div key={day.value} className="border-b border-mekomit-100 pb-4 last:border-b-0">
                                    <h3 className="text-lg font-bold text-mekomit-900 mb-3 flex items-center gap-2">
                                        <Calendar className="w-5 h-5" />
                                        {day.label}
                                    </h3>
                                    <div className="grid gap-2 grid-cols-3 sm:grid-cols-2 lg:grid-cols-3">
                                        {daySlots.map(slot => (
                                            <TimeSlotCard
                                                key={slot.slot_id}
                                                slot={slot}
                                                deletingId={deletingId}
                                                orderCount={orderCountsBySlot[slot.slot_id] || 0}
                                                onEdit={() => openEditModal(slot)}
                                                onDelete={() => handleDelete(slot)}
                                                onViewOrders={() => handleViewSlotOrders(slot)}
                                                onClick={() => handleSlotClick(slot)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {timeSlotsEnabled && orders.filter(o => o.time_slot_id).length > 0 && (
                <div className="flex justify-center">
                    <button
                        onClick={() => setShowAllTimeSlotsOrdersModal(true)}
                        className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white font-semibold shadow-md border border-mekomit-300 hover:from-mekomit-600 hover:to-mekomit-500 hover:shadow-lg transition-all"
                        title="צפה בכל ההזמנות"
                    >
                        <ShoppingCart className="w-5 h-5" />
                        <span>צפה בכל ההזמנות ({orders.filter(o => o.time_slot_id).length})</span>
                    </button>
                </div>
            )}

            {showModal && (
                <TimeSlotModal
                    storeId={storeId}
                    slot={editingSlot}
                    onClose={() => { setShowModal(false); setEditingSlot(null); }}
                    onSave={async () => {
                        setShowModal(false);
                        setEditingSlot(null);
                        await refetch();
                    }}
                    addSlot={addSlot}
                    updateSlot={updateSlot}
                    chatId={chatId}
                />
            )}

            {showOrdersModal && selectedSlotForOrders && (
                <SlotOrdersModal
                    slot={selectedSlotForOrders}
                    orders={ordersBySlot[selectedSlotForOrders.slot_id] || []}
                    onClose={() => { setShowOrdersModal(false); setSelectedSlotForOrders(null); }}
                    storeId={storeId}
                    chatId={chatId}
                    onRefresh={refetchOrders}
                />
            )}

            {showSlotDetailsModal && selectedSlotForDetails && (
                <SlotDetailsModal
                    slot={selectedSlotForDetails}
                    orderCount={orderCountsBySlot[selectedSlotForDetails.slot_id] || 0}
                    onClose={() => { setShowSlotDetailsModal(false); setSelectedSlotForDetails(null); }}
                    onEdit={() => {
                        setShowSlotDetailsModal(false);
                        openEditModal(selectedSlotForDetails);
                    }}
                    onDelete={() => {
                        setShowSlotDetailsModal(false);
                        handleDelete(selectedSlotForDetails);
                    }}
                    onViewOrders={() => {
                        setShowSlotDetailsModal(false);
                        handleViewSlotOrders(selectedSlotForDetails);
                    }}
                    deletingId={deletingId}
                />
            )}

            {showAllTimeSlotsOrdersModal && (
                <AllTimeSlotsOrdersModal
                    timeSlots={timeSlots}
                    ordersBySlot={ordersBySlot}
                    onClose={() => setShowAllTimeSlotsOrdersModal(false)}
                    storeId={storeId}
                    chatId={chatId}
                    onRefresh={refetchOrders}
                />
            )}
        </div>
    );
};

// Time Slot Card Component
const TimeSlotCard = ({ slot, deletingId, orderCount, onEdit, onDelete, onViewOrders, onClick }) => {
    const isAppt = useIsAppointments();
    // Calculate percentage filled
    const percentageFilled = slot.max_orders > 0 ? (orderCount / slot.max_orders) * 100 : 0;
    const isNearFull = percentageFilled >= 80;

    return (
        <>
            {/* Mobile view - minimal card */}
            <div
                className={`sm:hidden relative rounded-lg border p-3 shadow-sm active:scale-95 transition-all cursor-pointer ${slot.enabled ? 'border-mekomit-200 bg-gradient-to-br from-mekomit-50 to-white' : 'border-line-strong bg-surface-muted opacity-60'
                    }`}
                onClick={onClick}
            >
                {/* Order count badge */}
                {orderCount > 0 && (
                    <div
                        className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full shadow-md font-bold text-xs border-2 border-white ${isNearFull ? 'bg-danger-500 text-white' : 'bg-success-500 text-white'
                            }`}
                    >
                        {orderCount}
                    </div>
                )}

                <div className="text-center">
                    <div className="font-semibold text-mekomit-900 text-[11px] leading-tight">
                        {slot.start_time.slice(0, 5)}
                    </div>
                    <div className="text-[10px] text-mekomit-600">-</div>
                    <div className="font-semibold text-mekomit-900 text-[11px] leading-tight">
                        {slot.end_time.slice(0, 5)}
                    </div>
                </div>
            </div>

            {/* Desktop view - full card */}
            <div className={`hidden sm:block group relative rounded-lg border p-4 shadow-sm hover:shadow-md transition-all ${slot.enabled ? 'border-mekomit-200 bg-gradient-to-br from-mekomit-50 to-white' : 'border-line-strong bg-surface-muted opacity-60'
                }`}>
                {/* Order count badge */}
                {orderCount > 0 && (
                    <button
                        onClick={onViewOrders}
                        className={`absolute -top-2 -right-2 px-2.5 py-1 rounded-full shadow-md font-bold text-sm border-2 border-white cursor-pointer hover:scale-110 transition-transform ${isNearFull ? 'bg-danger-500 text-white hover:bg-danger-600' : 'bg-success-500 text-white hover:bg-success-600'
                            }`}
                        title="לחץ לצפייה בהזמנות"
                    >
                        {orderCount}
                    </button>
                )}

                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Clock className="w-5 h-5 text-mekomit-600 flex-shrink-0" />
                        <h4 className="font-semibold text-mekomit-900 truncate">
                            {slot.start_time} - {slot.end_time}
                        </h4>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={onEdit}
                            className="p-2 rounded-lg hover:bg-mekomit-100 text-mekomit-700"
                            title="ערוך"
                        >
                            <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onDelete}
                            disabled={deletingId === slot.slot_id}
                            className="p-2 rounded-lg hover:bg-danger-50 text-danger-600 disabled:opacity-50"
                            title="מחק"
                        >
                            {deletingId === slot.slot_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4" />
                            )}
                        </button>
                    </div>
                </div>
                {slot.label && (
                    <p className="text-sm text-mekomit-700 font-medium mb-2 truncate">{slot.label}</p>
                )}
                <div className="text-sm text-mekomit-600 space-y-1">
                    {isAppt ? (
                        // Appointment window: show booked count, not retail caps.
                        <div className="flex items-center gap-2 min-w-0">
                            <Users className="w-4 h-4 flex-shrink-0" />
                            <span>{orderCount > 0 ? `${orderCount} תורים נקבעו` : 'אין עדיין תורים'}</span>
                        </div>
                    ) : (
                    <>
                    <div className="flex items-center justify-between gap-1">
                        <div className="flex items-center gap-2 min-w-0">
                            <Users className="w-4 h-4 flex-shrink-0" />
                            <span>עד {slot.max_orders} הזמנות</span>
                        </div>
                        {orderCount > 0 && (
                            <span className={`text-xs font-bold flex-shrink-0 ${isNearFull ? 'text-danger-600' : 'text-success-600'}`}>
                                ({orderCount} פעילות)
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 flex-shrink-0" />
                        <span>עד {slot.max_items} פריטים</span>
                    </div>
                    </>
                    )}
                    {!slot.enabled && (
                        <div className="mt-2 text-xs text-danger-600 font-medium">לא פעיל</div>
                    )}
                </div>
            </div>
        </>
    );
};

// Time Slot Modal Component
const TimeSlotModal = ({ storeId, slot, onClose, onSave, addSlot, updateSlot, chatId }) => {
    const isAppt = useIsAppointments();
    const [formData, setFormData] = useState({
        day_of_week: slot?.day_of_week ?? 0,
        start_time: slot?.start_time || '08:00',
        end_time: slot?.end_time || '10:00',
        // Retail caps (orders/items) are meaningless for an appointment window —
        // default them to 0 (= no limit) and hide the inputs below.
        max_orders: slot?.max_orders ?? (isAppt ? 0 : 50),
        max_items: slot?.max_items ?? (isAppt ? 0 : 200),
        label: slot?.label || '',
        enabled: slot?.enabled ?? true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate times
            if (formData.start_time >= formData.end_time) {
                setError('שעת סיום חייבת להיות אחרי שעת התחלה');
                setLoading(false);
                return;
            }

            const slotData = {
                day_of_week: parseInt(formData.day_of_week),
                start_time: formData.start_time,
                end_time: formData.end_time,
                max_orders: parseInt(formData.max_orders),
                max_items: parseInt(formData.max_items),
                label: formData.label,
                enabled: formData.enabled
            };

            if (slot) {
                await updateSlot(slot.slot_id, slotData);
            } else {
                await addSlot(slotData);
            }

            onSave();
        } catch (err) {
            setError(err.message || 'שגיאה בשמירת משבצת הזמן');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-ink-strong">
                        {slot ? 'ערוך משבצת זמן' : 'הוסף משבצת זמן'}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-danger-50 border border-danger-100 rounded-lg text-danger-700 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">יום בשבוע</label>
                        <select
                            value={formData.day_of_week}
                            onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            required
                        >
                            {DAYS_OF_WEEK.map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">שעת התחלה</label>
                            <input
                                type="time"
                                value={formData.start_time}
                                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">שעת סיום</label>
                            <input
                                type="time"
                                value={formData.end_time}
                                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">כותרת (אופציונלי)</label>
                        <input
                            type="text"
                            value={formData.label}
                            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            placeholder={isAppt ? 'למשל: קבלת בוקר' : 'למשל: איסוף בוקר'}
                        />
                    </div>

                    {/* Retail capacity caps — irrelevant for an appointment window. */}
                    {!isAppt && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">מקסימום הזמנות</label>
                            <input
                                type="number"
                                min="1"
                                value={formData.max_orders}
                                onChange={(e) => setFormData({ ...formData, max_orders: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">מקסימום פריטים</label>
                            <input
                                type="number"
                                min="1"
                                value={formData.max_items}
                                onChange={(e) => setFormData({ ...formData, max_items: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                required
                            />
                        </div>
                    </div>
                    )}

                    <div className="flex items-center gap-2">
                        <input
                            id="enabled"
                            type="checkbox"
                            checked={formData.enabled}
                            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                            className="h-4 w-4"
                        />
                        <label htmlFor="enabled" className="text-sm text-ink">פעיל</label>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-line-strong rounded-lg hover:bg-surface-muted transition-colors text-ink-strong"
                            disabled={loading}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'שומר...' : (slot ? 'שמור' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Slot Details Modal Component - Shows full details on mobile
const SlotDetailsModal = ({ slot, orderCount, onClose, onEdit, onDelete, onViewOrders, deletingId }) => {
    const isAppt = useIsAppointments();
    const percentageFilled = slot.max_orders > 0 ? (orderCount / slot.max_orders) * 100 : 0;
    const isNearFull = percentageFilled >= 80;

    const getDayName = (dayNum) => {
        const day = DAYS_OF_WEEK.find(d => d.value === dayNum);
        return day ? day.label : '';
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-ink-strong">פרטי משבצת זמן</h3>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Time and Day */}
                    <div className="p-4 bg-mekomit-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-5 h-5 text-mekomit-600" />
                            <span className="font-semibold text-mekomit-900 text-lg">
                                {slot.start_time} - {slot.end_time}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-mekomit-700">
                            <Calendar className="w-4 h-4" />
                            <span>{getDayName(slot.day_of_week)}</span>
                        </div>
                        {slot.label && (
                            <div className="mt-2 text-sm text-mekomit-700 font-medium">{slot.label}</div>
                        )}
                    </div>

                    {/* Capacity */}
                    {isAppt ? (
                        <div className="p-4 bg-surface-muted rounded-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-ink-muted" />
                                    <span className="text-ink-strong">תורים</span>
                                </div>
                                <span className={`text-sm font-bold ${orderCount > 0 ? 'text-success-600' : 'text-ink-muted'}`}>
                                    {orderCount > 0 ? `${orderCount} נקבעו` : 'אין עדיין'}
                                </span>
                            </div>
                        </div>
                    ) : (
                    <div className="p-4 bg-surface-muted rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-ink-muted" />
                                <span className="text-ink-strong">הזמנות</span>
                            </div>
                            <div className="text-left">
                                {orderCount > 0 && (
                                    <span className={`text-sm font-bold ${isNearFull ? 'text-danger-600' : 'text-success-600'}`}>
                                        {orderCount} פעילות
                                    </span>
                                )}
                                <div className="text-ink-muted text-sm">
                                    עד {slot.max_orders}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Package className="w-5 h-5 text-ink-muted" />
                                <span className="text-ink-strong">פריטים</span>
                            </div>
                            <div className="text-ink-muted text-sm">
                                עד {slot.max_items}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Status */}
                    {!slot.enabled && (
                        <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                            <span className="text-danger-600 font-medium text-sm">משבצת לא פעילה</span>
                        </div>
                    )}

                    {/* View Orders Button */}
                    {orderCount > 0 && (
                        <button
                            onClick={onViewOrders}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-success-500 text-white rounded-lg hover:bg-success-600 transition-colors font-semibold"
                        >
                            <ShoppingCart className="w-5 h-5" />
                            <span>צפה בהזמנות ({orderCount})</span>
                        </button>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onEdit}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-mekomit-500 text-white rounded-lg hover:bg-mekomit-600 transition-colors font-semibold"
                        >
                            <Edit2 className="w-4 h-4" />
                            <span>ערוך</span>
                        </button>
                        <button
                            onClick={onDelete}
                            disabled={deletingId === slot.slot_id}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-danger-500 text-white rounded-lg hover:bg-danger-600 transition-colors font-semibold disabled:opacity-50"
                        >
                            {deletingId === slot.slot_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4" />
                            )}
                            <span>מחק</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// All Time Slots Orders Modal Component - Shows orders for all time slots grouped by slot
const AllTimeSlotsOrdersModal = ({ timeSlots, ordersBySlot, onClose, storeId, chatId, onRefresh }) => {
    const [expandedOrders, setExpandedOrders] = useState(new Set());
    const [expandedSlots, setExpandedSlots] = useState(new Set());

    const getDayName = (dayNum) => {
        const day = DAYS_OF_WEEK.find(d => d.value === dayNum);
        return day ? day.label : '';
    };

    const handlePrint = () => {
        // Expand all slots and orders before printing
        const allSlotIds = timeSlots.map(slot => slot.slot_id);
        const allOrderIds = Object.values(ordersBySlot).flat().map(order => order.order_id);
        setExpandedSlots(new Set(allSlotIds));
        setExpandedOrders(new Set(allOrderIds));

        // Wait for state to update, then print
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const toggleOrder = (orderId) => {
        setExpandedOrders(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) {
                next.delete(orderId);
            } else {
                next.add(orderId);
            }
            return next;
        });
    };

    const toggleSlot = (slotId) => {
        setExpandedSlots(prev => {
            const next = new Set(prev);
            if (next.has(slotId)) {
                next.delete(slotId);
            } else {
                next.add(slotId);
            }
            return next;
        });
    };

    const handleStatusUpdate = async (orderId, newStatus) => {
        try {
            await updateOrderStatus(storeId, orderId, newStatus, chatId);
            if (onRefresh) onRefresh();
        } catch (err) {
            alert('שגיאה בעדכון סטטוס ההזמנה: ' + err.message);
        }
    };

    const handlePaymentStatusUpdate = async (orderId, newPaymentStatus) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';
            const url = `${API_BASE_URL}/admin/store/${storeId}/orders/${orderId}${chatId ? `?user_id=${encodeURIComponent(chatId)}` : ''}`;
            const response = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ payment_status: newPaymentStatus })
            });
            if (!response.ok) throw new Error('Failed to update payment status');
            if (onRefresh) onRefresh();
        } catch (err) {
            alert('שגיאה בעדכון סטטוס התשלום: ' + err.message);
        }
    };

    const STATUS_META = {
        pending: { label: 'ממתין' },
        confirmed: { label: 'אושר' },
        completed: { label: 'הושלם' },
        cancelled: { label: 'בוטל' },
        archived: { label: 'ארכוב' }
    };

    // Filter slots that have orders
    const slotsWithOrders = timeSlots.filter(slot => ordersBySlot[slot.slot_id]?.length > 0);

    // Group slots by day
    const slotsByDay = slotsWithOrders.reduce((acc, slot) => {
        if (!acc[slot.day_of_week]) {
            acc[slot.day_of_week] = [];
        }
        acc[slot.day_of_week].push(slot);
        return acc;
    }, {});

    // Sort slots within each day by start_time
    Object.keys(slotsByDay).forEach(day => {
        slotsByDay[day].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });

    const totalOrders = Object.values(ordersBySlot).reduce((sum, orders) => sum + orders.length, 0);

    // Create print-optimized layout
    const renderPrintVersion = () => {
        // Get all orders from all slots and sort by timeslot date/time
        const allOrders = Object.values(ordersBySlot).flat().sort((a, b) => {
            // First sort by date
            const dateA = a.time_slot_date ? new Date(a.time_slot_date).getTime() : 0;
            const dateB = b.time_slot_date ? new Date(b.time_slot_date).getTime() : 0;
            if (dateA !== dateB) return dateA - dateB;

            // Then sort by delivery time
            const timeA = a.delivery_time || '';
            const timeB = b.delivery_time || '';
            return timeA.localeCompare(timeB);
        });

        return (
            <div className="print-only" style={{ padding: '0', margin: '0' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', paddingBottom: '6px', borderBottom: '2px solid #000' }}>
                    רשימת הזמנות - {allOrders.length} הזמנות
                </div>

                {allOrders.map((order, index) => {
                    const customerName = order.customer?.name || order.customer_name || 'לא צוין';
                    const customerPhone = order.customer?.phone || order.customer_phone || '';
                    const customerEmail = order.customer?.email || order.customer_email || '';
                    const customerAddress = order.customer_address || order.customer?.address || '';
                    const totalPrice = order.total || order.total_price || '0';
                    const paymentStatus = order.payment_status === 'paid' ? 'שולם' : 'ממתין לתשלום';

                    // Get delivery info
                    let deliveryInfo = '';
                    if (order.fulfillment_type === 'delivery_area') {
                        deliveryInfo = order.delivery_location_name || order.delivery_area_name || '';
                    } else if (order.delivery_point_name) {
                        deliveryInfo = order.delivery_point_name;
                    }

                    // Get timeslot info
                    const timeSlotDate = order.time_slot_date ? new Date(order.time_slot_date).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' }) : '';
                    const deliveryTime = order.delivery_time || '';

                    return (
                        <div key={order.order_id} style={{
                            marginBottom: '12px',
                            padding: '8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            pageBreakInside: 'avoid',
                            background: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                        }}>
                            {/* Header Row */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>
                                <div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '2px' }}>הזמנה #{index + 1}</div>
                                    <div style={{ fontSize: '9px', color: '#6b7280' }}>
                                        {new Date(order.created_at).toLocaleDateString('he-IL')} {new Date(order.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#f97316' }}>₪{totalPrice}</div>
                            </div>

                            {/* Customer Info */}
                            <div style={{ marginBottom: '6px', fontSize: '9px' }}>
                                <div style={{ marginBottom: '2px' }}>
                                    <span style={{ fontWeight: 'bold', display: 'inline-block', width: '70px' }}>לקוח:</span>
                                    <span>{customerName}</span>
                                </div>
                                {customerPhone && (
                                    <div style={{ marginBottom: '2px' }}>
                                        <span style={{ fontWeight: 'bold', display: 'inline-block', width: '70px' }}>טלפון:</span>
                                        <span dir="ltr" style={{ direction: 'ltr' }}>{customerPhone}</span>
                                    </div>
                                )}
                                {customerEmail && (
                                    <div style={{ marginBottom: '2px' }}>
                                        <span style={{ fontWeight: 'bold', display: 'inline-block', width: '70px' }}>אימייל:</span>
                                        <span>{customerEmail}</span>
                                    </div>
                                )}
                                {customerAddress && (
                                    <div style={{ marginBottom: '2px' }}>
                                        <span style={{ fontWeight: 'bold', display: 'inline-block', width: '70px' }}>כתובת:</span>
                                        <span>{customerAddress}</span>
                                    </div>
                                )}
                            </div>

                            {/* Delivery Info */}
                            {(deliveryInfo || (timeSlotDate && deliveryTime)) && (
                                <div style={{ marginBottom: '6px', fontSize: '9px', padding: '4px', background: '#fff7ed', borderRadius: '2px' }}>
                                    {deliveryInfo && (
                                        <div style={{ marginBottom: '2px' }}>
                                            <span style={{ fontWeight: 'bold', display: 'inline-block', width: '70px' }}>
                                                {order.fulfillment_type === 'delivery_area' ? 'משלוח:' : 'נקודת איסוף:'}
                                            </span>
                                            <span>{deliveryInfo}</span>
                                        </div>
                                    )}
                                    {timeSlotDate && deliveryTime && (
                                        <div>
                                            <span style={{ fontWeight: 'bold', display: 'inline-block', width: '70px' }}>מועד:</span>
                                            <span>{timeSlotDate} {deliveryTime}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Items */}
                            <div style={{ marginBottom: '6px' }}>
                                <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '3px' }}>פריטים:</div>
                                {order.items?.map((item, idx) => {
                                    const quantity = Number(item.quantity) || 1;
                                    const lineTotal = item.total_price != null ? Number(item.total_price) : (Number(item.unit_price) * quantity);
                                    const paidOptions = item.paid_options?.map(opt => opt.name).join(', ') || '';

                                    return (
                                        <div key={idx} style={{ fontSize: '8px', marginBottom: '2px', paddingRight: '8px' }}>
                                            <span style={{ fontWeight: 'bold' }}>{item.product_name}</span>
                                            {item.variant_name && <span style={{ color: '#6b7280' }}> ({item.variant_name})</span>}
                                            {paidOptions && <span style={{ color: '#059669' }}> + {paidOptions}</span>}
                                            <span style={{ float: 'left' }}>
                                                {quantity > 1 && <span>{quantity} × </span>}
                                                <span style={{ fontWeight: 'bold' }}>₪{lineTotal}</span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Payment & Notes */}
                            <div style={{ fontSize: '8px', paddingTop: '4px', borderTop: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                    <span>
                                        <span style={{ fontWeight: 'bold' }}>תשלום:</span> {paymentStatus}
                                        {order.payment_method && <span> ({order.payment_method})</span>}
                                    </span>
                                    <span>
                                        <span style={{ fontWeight: 'bold' }}>סטטוס:</span> {STATUS_META[order.status]?.label || order.status}
                                    </span>
                                </div>
                                {order.notes && (
                                    <div style={{ marginTop: '3px', padding: '3px', background: '#fef3c7', borderRadius: '2px' }}>
                                        <span style={{ fontWeight: 'bold' }}>הערות:</span> {order.notes}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <style>{`
                @media print {
                    /* Hide screen version */
                    .screen-only {
                        display: none !important;
                    }

                    /* Show print version */
                    .print-only {
                        display: block !important;
                    }

                    /* Hide buttons */
                    button, select, input {
                        display: none !important;
                    }

                    /* Simple container styling */
                    .print-container {
                        position: static !important;
                        background: white !important;
                    }

                    .print-modal {
                        position: static !important;
                        max-width: 100% !important;
                        box-shadow: none !important;
                        border-radius: 0 !important;
                    }

                    /* Page margins */
                    @page {
                        margin: 15mm;
                    }
                }

                /* Hide print version on screen */
                .print-only {
                    display: none;
                }
            `}</style>
            <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4 print-container" onClick={onClose}>
                <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col print-modal" onClick={(e) => e.stopPropagation()}>

                {/* Print version - table layout */}
                {renderPrintVersion()}

                {/* Screen version - card layout */}
                <div className="screen-only flex items-center justify-between p-6 border-b border-line-subtle bg-white rounded-t-xl shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-ink-strong">
                            כל ההזמנות במשבצות זמן
                        </h3>
                        <p className="text-sm text-ink-muted mt-1">
                            סה"כ {totalOrders} הזמנות ב-{slotsWithOrders.length} משבצות זמן
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="p-2 hover:bg-mekomit-50 rounded-lg transition-colors text-mekomit-600"
                            title="הדפס"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="screen-only flex-1 overflow-y-auto p-6">
                    {slotsWithOrders.length === 0 ? (
                        <div className="text-center py-12 text-ink-subtle">
                            <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-ink-subtle" />
                            <p>אין הזמנות במשבצות זמן</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {DAYS_OF_WEEK.map(day => {
                                const daySlots = slotsByDay[day.value] || [];
                                if (daySlots.length === 0) return null;

                                return (
                                    <div key={day.value} className="border border-mekomit-100 rounded-xl overflow-hidden">
                                        <div className="bg-mekomit-50 px-4 py-3 border-b border-mekomit-100">
                                            <h4 className="text-lg font-bold text-mekomit-900 flex items-center gap-2">
                                                <Calendar className="w-5 h-5" />
                                                {day.label}
                                            </h4>
                                        </div>
                                        <div className="p-4 space-y-4">
                                            {daySlots.map(slot => {
                                                const slotOrders = ordersBySlot[slot.slot_id] || [];
                                                const isSlotExpanded = expandedSlots.has(slot.slot_id);

                                                return (
                                                    <div key={slot.slot_id} className="border border-line rounded-lg overflow-hidden">
                                                        <div
                                                            className="bg-surface-muted px-4 py-3 cursor-pointer hover:bg-surface-muted transition-colors"
                                                            onClick={() => toggleSlot(slot.slot_id)}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                    <Clock className="w-4 h-4 text-mekomit-600" />
                                                                    <span className="font-semibold text-ink-strong">
                                                                        {slot.start_time} - {slot.end_time}
                                                                    </span>
                                                                    {slot.label && (
                                                                        <span className="text-sm text-ink-muted">• {slot.label}</span>
                                                                    )}
                                                                    <span className="text-sm font-bold text-success-600">
                                                                        {slotOrders.length} הזמנות
                                                                    </span>
                                                                </div>
                                                                <div className={`transform transition-transform duration-200 ${isSlotExpanded ? 'rotate-180' : ''}`}>
                                                                    <ChevronDown className="w-5 h-5 text-ink-subtle" />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {isSlotExpanded && (
                                                            <div className="p-4 space-y-3 bg-white">
                                                                {slotOrders.map((order) => {
                                                                    const isExpanded = expandedOrders.has(order.order_id);
                                                                    const customerName = order.customer?.name || order.customer_name || 'לא צוין';
                                                                    const customerPhone = order.customer?.phone || order.customer_phone || '';
                                                                    const customerEmail = order.customer?.email || order.customer_email || '';
                                                                    const totalPrice = order.total || order.total_price || '0';

                                                                    return (
                                                                        <div key={order.order_id} className={`group relative bg-white border rounded-2xl transition-all duration-300 overflow-hidden flex flex-col shadow-sm hover:shadow-lg ${isExpanded ? 'border-mekomit-300' : 'border-line hover:border-mekomit-200'}`}>
                                                                            <div className="p-5 cursor-pointer" onClick={() => toggleOrder(order.order_id)}>
                                                                                <div className="flex items-start justify-between gap-3">
                                                                                    <div className="flex-1 min-w-0">
                                                                                        {isExpanded ? (
                                                                                            <>
                                                                                                <div className="flex items-center gap-2 mb-1">
                                                                                                    <span className="font-bold text-ink-strong text-sm truncate">{customerName}</span>
                                                                                                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold shadow-xs tracking-wide ${getStatusBadgeClasses(order.status)}`}>{STATUS_META[order.status]?.label || order.status}</span>
                                                                                                </div>
                                                                                                <div className="text-sm font-medium text-mekomit-900 truncate" title={customerName}>{customerName}</div>
                                                                                                <div className="text-[11px] text-ink-subtle mt-0.5 flex items-center gap-2"><span>{new Date(order.created_at).toLocaleDateString('he-IL')}</span><span>•</span><span>{new Date(order.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</span></div>
                                                                                            </>
                                                                                        ) : (
                                                                                            <div className="flex items-center gap-2">
                                                                                                <span className="text-sm font-semibold text-mekomit-900 truncate" title={customerName}>{customerName}</span>
                                                                                                <span className={`text-[10px] px-2 py-1 rounded-full font-semibold shadow-xs tracking-wide ${getStatusBadgeClasses(order.status)}`}>{STATUS_META[order.status]?.label || order.status}</span>
                                                                                                {order.payment_status && (
                                                                                                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold shadow-xs tracking-wide ${order.payment_status === 'paid' ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'}`}>
                                                                                                        {order.payment_status === 'paid' ? 'שולם' : 'ממתין'}
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="text-left flex flex-col items-end gap-2">
                                                                                        <div className="text-xl font-bold text-mekomit-600">₪{totalPrice}</div>
                                                                                        <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}><svg className="w-5 h-5 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            {isExpanded && (
                                                                                <div className="px-5 pb-5 pt-0 border-t border-line-subtle animate-fade-in flex flex-col gap-5">
                                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                                        <div className="p-4 bg-mekomit-50 rounded-xl">
                                                                                            <h4 className="text-xs font-bold text-mekomit-900 mb-2">פרטי לקוח</h4>
                                                                                            <div className="space-y-1 text-xs">
                                                                                                <div className="flex justify-between"><span className="text-ink-muted">שם</span><span className="font-semibold text-ink-strong">{customerName}</span></div>
                                                                                                {(order.customer_address || order.customer?.address) && (
                                                                                                    <div className="flex justify-between"><span className="text-ink-muted">כתובת</span><span className="text-ink-strong truncate" title={order.customer_address || order.customer?.address}>{order.customer_address || order.customer?.address}</span></div>
                                                                                                )}
                                                                                                {customerPhone && (
                                                                                                    <div className="flex justify-between items-center">
                                                                                                        <span className="text-ink-muted">טלפון</span>
                                                                                                        <div className="flex items-center gap-2">
                                                                                                            <span className="font-medium text-ink-strong font-mono" dir="ltr">{customerPhone}</span>
                                                                                                            {isMobilePhone(customerPhone) && (
                                                                                                                <a
                                                                                                                    href={`https://wa.me/${formatPhoneForWhatsApp(customerPhone)}`}
                                                                                                                    target="_blank"
                                                                                                                    rel="noopener noreferrer"
                                                                                                                    className="inline-flex items-center justify-center p-1.5 rounded-full bg-success-500 hover:bg-success-600 text-white transition-colors"
                                                                                                                    title="פתח שיחה בוואטסאפ"
                                                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                                                >
                                                                                                                    <MessageCircle className="w-3.5 h-3.5" />
                                                                                                                </a>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                                {customerEmail && <div className="flex justify-between"><span className="text-ink-muted">אימייל</span><span className="text-ink-strong truncate" title={customerEmail}>{customerEmail}</span></div>}
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="p-4 bg-surface-muted rounded-xl">
                                                                                            <h4 className="text-xs font-bold text-ink-strong mb-2">תשלום ומשלוח</h4>
                                                                                            <div className="space-y-1 text-xs">
                                                                                                {order.payment_method && <div className="flex justify-between"><span className="text-ink-muted">תשלום</span><span className="font-medium text-ink-strong">{order.payment_method}</span></div>}
                                                                                                {order.payment_status && (
                                                                                                    <div className="space-y-1">
                                                                                                        <div className="flex justify-between"><span className="text-ink-muted">סטטוס תשלום</span><span className={`font-semibold ${order.payment_status === 'paid' ? 'text-success-600' : order.payment_status === 'refunded' ? 'text-info-600' : 'text-warning-600'}`}>{order.payment_status === 'paid' ? 'שולם' : 'ממתין לתשלום'}</span></div>
                                                                                                        <div>
                                                                                                            <label htmlFor={`payment-status-${order.order_id}`} className="sr-only">עדכן סטטוס תשלום להזמנה #{order.order_id}</label>
                                                                                                            <select id={`payment-status-${order.order_id}`} value={order.payment_status} onChange={(e) => handlePaymentStatusUpdate(order.order_id, e.target.value)} className="w-full text-[11px] px-2 py-1 rounded-md border border-line-strong bg-white focus:outline-none focus:ring-2 focus:ring-mekomit-400 font-medium">
                                                                                                                <option value="pending">ממתין</option>
                                                                                                                <option value="paid">שולם</option>
                                                                                                            </select>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                                {(() => {
                                                                                                    if (order.fulfillment_type === 'delivery_area') {
                                                                                                        const areaName = order.delivery_location_name || order.delivery_area_name;
                                                                                                        if (!areaName) return null;
                                                                                                        return (
                                                                                                            <div className="flex justify-between">
                                                                                                                <span className="text-ink-muted">משלוח ליישוב</span>
                                                                                                                <span className="font-medium text-info-700" title={areaName}>{areaName}</span>
                                                                                                            </div>
                                                                                                        );
                                                                                                    } else {
                                                                                                        if (!order.delivery_point_name) return null;
                                                                                                        return (
                                                                                                            <div className="flex justify-between">
                                                                                                                <span className="text-ink-muted">נקודת חלוקה</span>
                                                                                                                <span className="font-medium text-info-700" title={order.delivery_point_name}>{order.delivery_point_name}</span>
                                                                                                            </div>
                                                                                                        );
                                                                                                    }
                                                                                                })()}
                                                                                                {order.time_slot_date && order.delivery_time && (
                                                                                                    <div className="flex justify-between">
                                                                                                        <span className="text-ink-muted">זמן איסוף</span>
                                                                                                        <span className="font-medium text-mekomit-700" title={`${order.time_slot_date} ${order.delivery_time}`}>
                                                                                                            {new Date(order.time_slot_date).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' })} {order.delivery_time}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h4 className="text-xs font-bold text-ink-strong mb-2">פריטים ({order.items?.length || 0})</h4>
                                                                                        <div className="space-y-2">
                                                                                            {order.items?.map((item, idx) => {
                                                                                                const quantity = Number(item.quantity) || 1;
                                                                                                const lineTotal = (() => { const raw = item.total_price != null ? Number(item.total_price) : (Number(item.unit_price) * quantity); return isNaN(raw) ? 0 : raw; })();
                                                                                                const unitPrice = (() => { const raw = item.unit_price != null ? Number(item.unit_price) : (lineTotal / quantity); return isNaN(raw) ? lineTotal : raw; })();
                                                                                                return (
                                                                                                    <div key={idx} className="flex justify-between items-start text-xs p-3 bg-white rounded-lg border border-line">
                                                                                                        <div className="flex-1 min-w-0">
                                                                                                            <div className="font-semibold text-ink-strong truncate" title={item.product_name}>{item.product_name}</div>
                                                                                                            {item.variant_name && <div className="text-[10px] text-ink-muted mt-0.5">{item.variant_name}</div>}
                                                                                                            {item.paid_options?.length > 0 && (<div className="text-[10px] text-ink-subtle mt-0.5">{item.paid_options.map(opt => opt.name).join(', ')}</div>)}
                                                                                                        </div>
                                                                                                        <div className="text-right ml-3 shrink-0 flex flex-col items-end">
                                                                                                            {quantity > 1 ? (
                                                                                                                <div className="text-ink-muted whitespace-nowrap" title={`${quantity} × ₪${unitPrice.toFixed(2)}`}>{quantity} × ₪{unitPrice % 1 === 0 ? unitPrice : unitPrice.toFixed(2)}</div>
                                                                                                            ) : (
                                                                                                                <div className="text-ink-muted" title={`₪${unitPrice.toFixed(2)}`}>₪{unitPrice % 1 === 0 ? unitPrice : unitPrice.toFixed(2)}</div>
                                                                                                            )}
                                                                                                            <div className="font-bold text-ink-strong" title={`₪${lineTotal.toFixed(2)}`}>₪{lineTotal % 1 === 0 ? lineTotal : lineTotal.toFixed(2)}</div>
                                                                                                            {quantity > 1 && (<div className="text-[10px] text-ink-subtle mt-0.5" aria-hidden="true">= ₪{lineTotal % 1 === 0 ? lineTotal : lineTotal.toFixed(2)}</div>)}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                            {(!order.items || order.items.length === 0) && (<div className="p-3 text-[11px] text-ink-subtle bg-surface-muted border border-line rounded-lg text-center">אין פריטים</div>)}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="p-4 bg-surface-muted rounded-xl text-xs space-y-1">
                                                                                        <div className="flex justify-between text-ink-muted"><span>סכום ביניים</span><span>₪{order.subtotal || order.total}</span></div>
                                                                                        {order.discount && parseFloat(order.discount) > 0 && <div className="flex justify-between text-success-600"><span>הנחה</span><span>-₪{order.discount}</span></div>}
                                                                                        {order.delivery_fee && parseFloat(order.delivery_fee) > 0 && <div className="flex justify-between text-ink-muted"><span>דמי משלוח</span><span>₪{order.delivery_fee}</span></div>}
                                                                                        <div className="flex justify-between font-bold text-ink-strong text-sm pt-2 border-t border-line"><span>סה"כ</span><span>₪{totalPrice}</span></div>
                                                                                    </div>
                                                                                    <div>
                                                                                        {order.status === 'archived' ? (
                                                                                            <div className="flex flex-col gap-2">
                                                                                                <div className="text-xs font-semibold text-ink">הזמנה בארכיון</div>
                                                                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleStatusUpdate(order.order_id, 'pending'); }} className="px-4 py-2 rounded-lg bg-success-600 hover:bg-success-700 text-white text-xs font-bold focus:outline-none focus:ring-2 focus:ring-success-300 transition-colors">שחזר להזמנות פעילות</button>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <>
                                                                                                <label className="block text-xs font-semibold text-ink mb-1">עדכן סטטוס</label>
                                                                                                <select value={order.status} onChange={(e) => handleStatusUpdate(order.order_id, e.target.value)} onClick={(e) => e.stopPropagation()} className={`w-full px-3 py-2 rounded-lg text-xs font-semibold border border-transparent focus:border-transparent focus:ring-2 transition-colors ${getStatusSelectClasses(order.status)}`}>
                                                                                                    <option value="pending">ממתין</option>
                                                                                                    <option value="confirmed">אושר</option>
                                                                                                </select>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                    {order.notes && (<div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-[11px] leading-relaxed"><span className="font-semibold text-warning-900">הערות: </span><span className="text-warning-800">{order.notes}</span></div>)}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="screen-only p-6 border-t border-line-subtle bg-surface-muted rounded-b-xl shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-mekomit-500 text-white rounded-lg hover:bg-mekomit-600 transition-colors font-semibold shadow-sm"
                    >
                        סגור
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

// Helper functions from OrdersTab
const isMobilePhone = (phone) => {
    if (!phone) return false;
    const cleaned = phone.replace(/[\s\-()]/g, '');
    return /^(05\d{8}|(\+972|972)5\d{8})$/.test(cleaned);
};

const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/[\s\-()]/g, '');
    if (cleaned.startsWith('05')) return '972' + cleaned.substring(1);
    if (cleaned.startsWith('+972')) return cleaned.substring(1);
    if (cleaned.startsWith('972')) return cleaned;
    return cleaned;
};

const getStatusBadgeClasses = (status) => {
    const map = {
        pending: 'bg-warning-500 text-white',
        confirmed: 'bg-info-500 text-white',
        completed: 'bg-success-600 text-white',
        cancelled: 'bg-danger-600 text-white',
        archived: 'bg-line-strong text-white'
    };
    return map[status] || 'bg-line-strong text-white';
};

const getStatusSelectClasses = (status) => {
    const map = {
        pending: 'bg-warning-100 text-warning-800 focus:ring-warning-500',
        confirmed: 'bg-info-100 text-info-800 focus:ring-info-500',
        completed: 'bg-success-100 text-success-800 focus:ring-success-500'
    };
    return map[status] || 'bg-surface-muted text-ink-strong focus:ring-ink-subtle';
};

// Slot Orders Modal Component - Shows all orders for a specific time slot
const SlotOrdersModal = ({ slot, orders, onClose, storeId, chatId, onRefresh }) => {
    const isAppt = useIsAppointments();
    const [expandedOrders, setExpandedOrders] = useState(new Set());

    const getDayName = (dayNum) => {
        const day = DAYS_OF_WEEK.find(d => d.value === dayNum);
        return day ? day.label : '';
    };

    const toggleOrder = (orderId) => {
        setExpandedOrders(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) {
                next.delete(orderId);
            } else {
                next.add(orderId);
            }
            return next;
        });
    };

    const handleStatusUpdate = async (orderId, newStatus) => {
        try {
            await updateOrderStatus(storeId, orderId, newStatus, chatId);
            if (onRefresh) onRefresh();
        } catch (err) {
            alert('שגיאה בעדכון סטטוס ההזמנה: ' + err.message);
        }
    };

    const handlePaymentStatusUpdate = async (orderId, newPaymentStatus) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';
            const url = `${API_BASE_URL}/admin/store/${storeId}/orders/${orderId}${chatId ? `?user_id=${encodeURIComponent(chatId)}` : ''}`;
            const response = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ payment_status: newPaymentStatus })
            });
            if (!response.ok) throw new Error('Failed to update payment status');
            if (onRefresh) onRefresh();
        } catch (err) {
            alert('שגיאה בעדכון סטטוס התשלום: ' + err.message);
        }
    };

    const STATUS_META = {
        pending: { label: 'ממתין' },
        confirmed: { label: 'אושר' },
        completed: { label: 'הושלם' },
        cancelled: { label: 'בוטל' },
        archived: { label: 'ארכוב' }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-line-subtle bg-white rounded-t-xl shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-ink-strong">
                            הזמנות למשבצת זמן
                        </h3>
                        <p className="text-sm text-ink-muted mt-1">
                            {getDayName(slot.day_of_week)}, {slot.start_time} - {slot.end_time}
                            {slot.label && ` • ${slot.label}`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {orders.length === 0 ? (
                        <div className="text-center py-12 text-ink-subtle">
                            <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-ink-subtle" />
                            <p>אין הזמנות למשבצת זמן זו</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {orders.map((order) => {
                                const isExpanded = expandedOrders.has(order.order_id);
                                const customerName = order.customer?.name || order.customer_name || 'לא צוין';
                                const customerPhone = order.customer?.phone || order.customer_phone || '';
                                const customerEmail = order.customer?.email || order.customer_email || '';
                                const totalPrice = order.total || order.total_price || '0';

                                return (
                                    <div key={order.order_id} className={`group relative bg-white border rounded-2xl transition-all duration-300 overflow-hidden flex flex-col shadow-sm hover:shadow-lg ${isExpanded ? 'border-mekomit-300' : 'border-mekomit-100 hover:border-mekomit-200'}`}>
                                        <div className="p-5 cursor-pointer" onClick={() => toggleOrder(order.order_id)}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    {isExpanded ? (
                                                        <>
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-bold text-ink-strong text-sm truncate">{customerName}</span>
                                                                <span className={`text-[10px] px-2 py-1 rounded-full font-semibold shadow-xs tracking-wide ${getStatusBadgeClasses(order.status)}`}>{STATUS_META[order.status]?.label || order.status}</span>
                                                            </div>
                                                            <div className="text-sm font-medium text-mekomit-900 truncate" title={customerName}>{customerName}</div>
                                                            <div className="text-[11px] text-ink-subtle mt-0.5 flex items-center gap-2"><span>{new Date(order.created_at).toLocaleDateString('he-IL')}</span><span>•</span><span>{new Date(order.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</span></div>
                                                        </>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-mekomit-900 truncate" title={customerName}>{customerName}</span>
                                                            <span className={`text-[10px] px-2 py-1 rounded-full font-semibold shadow-xs tracking-wide ${getStatusBadgeClasses(order.status)}`}>{STATUS_META[order.status]?.label || order.status}</span>
                                                            {order.payment_status && (
                                                                <span className={`text-[10px] px-2 py-1 rounded-full font-semibold shadow-xs tracking-wide ${order.payment_status === 'paid' ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'}`}>
                                                                    {order.payment_status === 'paid' ? 'שולם' : 'ממתין'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-left flex flex-col items-end gap-2">
                                                    <div className="text-xl font-bold text-mekomit-600">₪{totalPrice}</div>
                                                    <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}><svg className="w-5 h-5 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></div>
                                                </div>
                                            </div>
                                        </div>
                                        {isExpanded && (
                                            <div className="px-5 pb-5 pt-0 border-t border-line-subtle animate-fade-in flex flex-col gap-5">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="p-4 bg-mekomit-50 rounded-xl">
                                                        <h4 className="text-xs font-bold text-mekomit-900 mb-2">פרטי לקוח</h4>
                                                        <div className="space-y-1 text-xs">
                                                            <div className="flex justify-between"><span className="text-ink-muted">שם</span><span className="font-semibold text-ink-strong">{customerName}</span></div>
                                                            {(order.customer_address || order.customer?.address) && (
                                                                <div className="flex justify-between"><span className="text-ink-muted">כתובת</span><span className="text-ink-strong truncate" title={order.customer_address || order.customer?.address}>{order.customer_address || order.customer?.address}</span></div>
                                                            )}
                                                            {customerPhone && (
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-ink-muted">טלפון</span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium text-ink-strong font-mono" dir="ltr">{customerPhone}</span>
                                                                        {isMobilePhone(customerPhone) && (
                                                                            <a
                                                                                href={`https://wa.me/${formatPhoneForWhatsApp(customerPhone)}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="inline-flex items-center justify-center p-1.5 rounded-full bg-success-500 hover:bg-success-600 text-white transition-colors"
                                                                                title="פתח שיחה בוואטסאפ"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <MessageCircle className="w-3.5 h-3.5" />
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {customerEmail && <div className="flex justify-between"><span className="text-ink-muted">אימייל</span><span className="text-ink-strong truncate" title={customerEmail}>{customerEmail}</span></div>}
                                                        </div>
                                                    </div>
                                                    <div className="p-4 bg-surface-muted rounded-xl">
                                                        <h4 className="text-xs font-bold text-ink-strong mb-2">תשלום ומשלוח</h4>
                                                        <div className="space-y-1 text-xs">
                                                            {order.payment_method && <div className="flex justify-between"><span className="text-ink-muted">תשלום</span><span className="font-medium text-ink-strong">{order.payment_method}</span></div>}
                                                            {order.payment_status && (
                                                                <div className="space-y-1">
                                                                    <div className="flex justify-between"><span className="text-ink-muted">סטטוס תשלום</span><span className={`font-semibold ${order.payment_status === 'paid' ? 'text-success-600' : order.payment_status === 'refunded' ? 'text-info-600' : 'text-warning-600'}`}>{order.payment_status === 'paid' ? 'שולם' : 'ממתין לתשלום'}</span></div>
                                                                    <div>
                                                                        <label htmlFor={`payment-status-${order.order_id}`} className="sr-only">עדכן סטטוס תשלום להזמנה #{order.order_id}</label>
                                                                        <select id={`payment-status-${order.order_id}`} value={order.payment_status} onChange={(e) => handlePaymentStatusUpdate(order.order_id, e.target.value)} className="w-full text-[11px] px-2 py-1 rounded-md border border-line-strong bg-white focus:outline-none focus:ring-2 focus:ring-mekomit-400 font-medium">
                                                                            <option value="pending">ממתין</option>
                                                                            <option value="paid">שולם</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {(() => {
                                                                if (order.fulfillment_type === 'delivery_area') {
                                                                    const areaName = order.delivery_location_name || order.delivery_area_name;
                                                                    if (!areaName) return null;
                                                                    return (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-ink-muted">משלוח ליישוב</span>
                                                                            <span className="font-medium text-info-700" title={areaName}>{areaName}</span>
                                                                        </div>
                                                                    );
                                                                } else {
                                                                    if (!order.delivery_point_name) return null;
                                                                    return (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-ink-muted">נקודת חלוקה</span>
                                                                            <span className="font-medium text-info-700" title={order.delivery_point_name}>{order.delivery_point_name}</span>
                                                                        </div>
                                                                    );
                                                                }
                                                            })()}
                                                            {order.time_slot_date && order.delivery_time && (
                                                                <div className="flex justify-between">
                                                                    <span className="text-ink-muted">זמן איסוף</span>
                                                                    <span className="font-medium text-mekomit-700" title={`${order.time_slot_date} ${order.delivery_time}`}>
                                                                        {new Date(order.time_slot_date).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' })} {order.delivery_time}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="text-xs font-bold text-ink-strong mb-2">פריטים ({order.items?.length || 0})</h4>
                                                    <div className="space-y-2">
                                                        {order.items?.map((item, idx) => {
                                                            const quantity = Number(item.quantity) || 1;
                                                            const lineTotal = (() => { const raw = item.total_price != null ? Number(item.total_price) : (Number(item.unit_price) * quantity); return isNaN(raw) ? 0 : raw; })();
                                                            const unitPrice = (() => { const raw = item.unit_price != null ? Number(item.unit_price) : (lineTotal / quantity); return isNaN(raw) ? lineTotal : raw; })();
                                                            return (
                                                                <div key={idx} className="flex justify-between items-start text-xs p-3 bg-white rounded-lg border border-line">
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="font-semibold text-ink-strong truncate" title={item.product_name}>{item.product_name}</div>
                                                                        {item.variant_name && <div className="text-[10px] text-ink-muted mt-0.5">{item.variant_name}</div>}
                                                                        {item.paid_options?.length > 0 && (<div className="text-[10px] text-ink-subtle mt-0.5">{item.paid_options.map(opt => opt.name).join(', ')}</div>)}
                                                                    </div>
                                                                    <div className="text-right ml-3 shrink-0 flex flex-col items-end">
                                                                        {quantity > 1 ? (
                                                                            <div className="text-ink-muted whitespace-nowrap" title={`${quantity} × ₪${unitPrice.toFixed(2)}`}>{quantity} × ₪{unitPrice % 1 === 0 ? unitPrice : unitPrice.toFixed(2)}</div>
                                                                        ) : (
                                                                            <div className="text-ink-muted" title={`₪${unitPrice.toFixed(2)}`}>₪{unitPrice % 1 === 0 ? unitPrice : unitPrice.toFixed(2)}</div>
                                                                        )}
                                                                        <div className="font-bold text-ink-strong" title={`₪${lineTotal.toFixed(2)}`}>₪{lineTotal % 1 === 0 ? lineTotal : lineTotal.toFixed(2)}</div>
                                                                        {quantity > 1 && (<div className="text-[10px] text-ink-subtle mt-0.5" aria-hidden="true">= ₪{lineTotal % 1 === 0 ? lineTotal : lineTotal.toFixed(2)}</div>)}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {(!order.items || order.items.length === 0) && (<div className="p-3 text-[11px] text-ink-subtle bg-surface-muted border border-line rounded-lg text-center">אין פריטים</div>)}
                                                    </div>
                                                </div>
                                                <div className="p-4 bg-surface-muted rounded-xl text-xs space-y-1">
                                                    <div className="flex justify-between text-ink-muted"><span>סכום ביניים</span><span>₪{order.subtotal || order.total}</span></div>
                                                    {order.discount && parseFloat(order.discount) > 0 && <div className="flex justify-between text-success-600"><span>הנחה</span><span>-₪{order.discount}</span></div>}
                                                    {order.delivery_fee && parseFloat(order.delivery_fee) > 0 && <div className="flex justify-between text-ink-muted"><span>דמי משלוח</span><span>₪{order.delivery_fee}</span></div>}
                                                    <div className="flex justify-between font-bold text-ink-strong text-sm pt-2 border-t border-line"><span>סה"כ</span><span>₪{totalPrice}</span></div>
                                                </div>
                                                <div>
                                                    {order.status === 'archived' ? (
                                                        <div className="flex flex-col gap-2">
                                                            <div className="text-xs font-semibold text-ink">הזמנה בארכיון</div>
                                                            <button type="button" onClick={(e) => { e.stopPropagation(); handleStatusUpdate(order.order_id, 'pending'); }} className="px-4 py-2 rounded-lg bg-success-600 hover:bg-success-700 text-white text-xs font-bold focus:outline-none focus:ring-2 focus:ring-success-300 transition-colors">שחזר להזמנות פעילות</button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <label className="block text-xs font-semibold text-ink mb-1">עדכן סטטוס</label>
                                                            <select value={order.status} onChange={(e) => handleStatusUpdate(order.order_id, e.target.value)} onClick={(e) => e.stopPropagation()} className={`w-full px-3 py-2 rounded-lg text-xs font-semibold border border-transparent focus:border-transparent focus:ring-2 transition-colors ${getStatusSelectClasses(order.status)}`}>
                                                                <option value="pending">ממתין</option>
                                                                <option value="confirmed">אושר</option>
                                                            </select>
                                                        </>
                                                    )}
                                                </div>
                                                {order.notes && (<div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-[11px] leading-relaxed"><span className="font-semibold text-warning-900">הערות: </span><span className="text-warning-800">{order.notes}</span></div>)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-line-subtle bg-surface-muted rounded-b-xl shrink-0">
                    <div className="flex items-center justify-between mb-4">
                        {isAppt ? (
                            <div className="text-sm text-ink">
                                <span className="font-semibold">סה"כ תורים:</span> {orders.length}
                            </div>
                        ) : (
                        <>
                        <div className="text-sm text-ink">
                            <span className="font-semibold">סה"כ הזמנות:</span> {orders.length} / {slot.max_orders}
                        </div>
                        <div className="text-sm text-ink">
                            <span className="font-semibold">סה"כ פריטים:</span> {orders.reduce((sum, o) => sum + (o.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 0), 0)} / {slot.max_items}
                        </div>
                        </>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-mekomit-500 text-white rounded-lg hover:bg-mekomit-600 transition-colors font-semibold shadow-sm"
                    >
                        סגור
                    </button>
                </div>
            </div>
        </div>
    );
};
